/**
 * The pacing gap, converted between what the operator types and what the column
 * stores.
 *
 * Hours is the stored unit because a month and a year are not fixed spans, so
 * the column cannot hold "2 months" without deciding how long a month is. The
 * admin decides once, here, and stores the product.
 */

/** The units the operator can author the gap in. */
export type PacingGapUnit = 'hours' | 'days' | 'weeks' | 'months' | 'years'

/**
 * Hours per unit. A month is 30 days and a year is 365 — the conversion a
 * campaign calendar wants, not a calendar-aware one: the gap is a coarse pacing
 * rule, and "2 months" meaning 1440 hours whatever month it lands in is easier
 * to reason about than a span that changes with February.
 */
const HOURS_PER_UNIT: Record<PacingGapUnit, number> = {
  hours: 1,
  days: 24,
  weeks: 24 * 7,
  months: 24 * 30,
  years: 24 * 365,
}

/**
 * Largest first, which is what makes `pacingGapFromHours` pick the unit the
 * operator most likely typed: 336 hours reads back as "2 weeks", not "336
 * hours".
 */
const UNITS_LARGEST_FIRST: readonly PacingGapUnit[] = ['years', 'months', 'weeks', 'days', 'hours']

export const PACING_GAP_UNITS: readonly PacingGapUnit[] = [
  'hours',
  'days',
  'weeks',
  'months',
  'years',
]

export const PACING_GAP_UNIT_LABEL: Record<PacingGapUnit, string> = {
  hours: 'hours',
  days: 'days',
  weeks: 'weeks',
  months: 'months (30 days)',
  years: 'years (365 days)',
}

/** The contract's ceiling on `min_hours_since_previous` — ten years. */
export const PACING_GAP_MAX_HOURS = 87_600

/**
 * The unit a blank gap field starts in.
 *
 * Days, because a campaign is paced in days: an operator who wants one banner a
 * week types 7 days, and the hours default made them convert first. It only ever
 * decides the EMPTY field — a stored gap still reads back in its own largest
 * whole unit, which `pacingGapFromHours` picks from the hours themselves.
 */
export const DEFAULT_PACING_GAP_UNIT: PacingGapUnit = 'days'

/** The form's two controls for the gap: a whole number and the unit it is in. */
export type PacingGapFields = {
  /** Blank means no gap. Held as a string like every other numeric field here. */
  value: string
  unit: PacingGapUnit
}

/**
 * The stored hours the authored pair comes to, or null for "no gap".
 *
 * Null rather than 0 when the operator clears the field: the BE accepts 0 and
 * treats it as no gap, but null is what "unset" means on a nullable column, and
 * it keeps the stored row readable to anyone who looks at it later.
 *
 * Anything that is not a whole number of at least one, or that overruns the
 * column, is also null. The form reports those as a field message while the
 * control is on the page; this is the second line of defence for a value
 * stranded by a placement switch, the same job `contractAutoClose` does.
 */
export const pacingGapToHours = (fields: PacingGapFields): number | null => {
  const trimmed = fields.value.trim()

  if (trimmed === '') {
    return null
  }

  const parsed = Number(trimmed)

  if (!Number.isInteger(parsed) || parsed < 1) {
    return null
  }

  const hours = parsed * HOURS_PER_UNIT[fields.unit]

  return hours > PACING_GAP_MAX_HOURS ? null : hours
}

/**
 * The stored hours, read back as the largest unit that divides them evenly.
 *
 * Without this a gap authored as "2 weeks" reopens as "336 hours", and the
 * operator either re-derives the number they typed or quietly saves a different
 * one. An hour count no larger unit divides stays in hours, which is always
 * exact.
 */
export const pacingGapFromHours = (hours: number | null | undefined): PacingGapFields => {
  // Zero is how the BE spells "no gap" for a row that carries the column, so it
  // reads back as the cleared field rather than as a gap of zero hours.
  if (hours === null || hours === undefined || hours <= 0 || !Number.isInteger(hours)) {
    return { value: '', unit: DEFAULT_PACING_GAP_UNIT }
  }

  const unit =
    UNITS_LARGEST_FIRST.find((candidate) => hours % HOURS_PER_UNIT[candidate] === 0) ?? 'hours'

  return { value: String(hours / HOURS_PER_UNIT[unit]), unit }
}

/**
 * The stored gap as the form understands it, for diffing a PATCH against.
 *
 * The BE accepts 0 and treats it as no gap, and the form shows it as the cleared
 * field — so a row carrying 0 must not read as a change every time an operator
 * opens it and saves something else. Routing it through the pair the form holds
 * is what makes the comparison agree with what is on the screen.
 */
export const storedPacingGapHours = (hours: number | null | undefined): number | null =>
  pacingGapToHours(pacingGapFromHours(hours))

/**
 * Each unit as a noun a sentence can use, singular and plural.
 *
 * Separate from `PACING_GAP_UNIT_LABEL` because that one is a dropdown option and
 * carries the conversion in brackets — "6 months (30 days)" would be unreadable
 * in a table cell, and "1 hours" is not English.
 */
const PACING_GAP_UNIT_NOUN: Record<PacingGapUnit, { one: string; many: string }> = {
  hours: { one: 'hour', many: 'hours' },
  days: { one: 'day', many: 'days' },
  weeks: { one: 'week', many: 'weeks' },
  months: { one: 'month', many: 'months' },
  years: { one: 'year', many: 'years' },
}

/**
 * The stored gap as a phrase to read, or null when the row paces nothing.
 *
 * Goes through `pacingGapFromHours`, so a row reads in the LARGEST whole unit
 * that divides it: "2 weeks", never "336 hours". That is the unit its author
 * most likely typed, but not always the one they typed — 14 days reads back as 2
 * weeks. Null is every hour count the form treats as no gap: unset, zero,
 * negative, and fractional.
 */
export const formatPacingGap = (hours: number | null | undefined): string | null => {
  const { value, unit } = pacingGapFromHours(hours)

  if (value === '') {
    return null
  }

  const noun = PACING_GAP_UNIT_NOUN[unit]

  return `${value} ${value === '1' ? noun.one : noun.many}`
}

/** The largest whole number of this unit the column can hold. */
export const pacingGapMaxValue = (unit: PacingGapUnit): number =>
  Math.floor(PACING_GAP_MAX_HOURS / HOURS_PER_UNIT[unit])
